import { isHotspotAtVideoEnd, isTransitionKeptAfterEnd } from '@/lib/hotspotGeometry'
import { ButtonActionType, type PlayerConfig, type PlayerState } from '@/types/interactiveVideo'
import { List } from 'lucide-react'
import { FC, RefObject, useState } from 'react'
import { createPortal } from 'react-dom'
import { isCueTooLate, isWithinEndLead } from '../branchChoiceTiming'
import type ButtonOverlay from '../ButtonOverlay'
import { SPSurveyLayer, SurveySheetHost } from '../modal/ModalSurvey'
import type ProgressBar from '../ProgressBar'
import PanelSurvey from '../survey/PanelSurvey'
import SurveyGate from '../survey/SurveyGate'
import type { RoutingAnswer } from '../survey/surveyRouting'
import type { SurveySheetState } from '../survey/useSurveySheet'
import type VideoCanvas from '../VideoCanvas'
import WideAgendaPanel, {
  findCurrentAgendaEntryId,
  formatAgendaPosition,
  isShownOnVideoWithAgenda,
  toAgendaEntries,
  WIDE_AGENDA_OVERLAY_BACKGROUND,
  WIDE_AGENDA_PANEL_WIDTH,
  WIDE_AGENDA_SIDE_WIDTH_CSS,
} from '../wide/WideAgendaPanel'
import WideAgendaButton from '../wide/WideAgendaButton'
import WideBackButton from '../wide/WideBackButton'
import WideBranchOverlay from '../wide/WideBranchOverlay'
import WideControlsBar, { WIDE_CONTROLS_CLEARANCE } from '../wide/WideControlsBar'
import WideEndScreen from '../wide/WideEndScreen'
import WideHotspotLayer from '../wide/WideHotspotLayer'
import WideStackedPanel from '../wide/WideStackedPanel'
import WideSurveyEntryButton from '../wide/WideSurveyEntryButton'
import VideoControlsPanel from './VideoControlsPanel'

export interface InteractiveVideoPlayerPresentationProps {
  config: PlayerConfig
  state: PlayerState
  /** 回答保存 API 用のシナリオID（SURVEYノードのゲート送信に使う） */
  scenarioId?: string
  /** 同一視聴セッションで回答済みの設問 id 集合（SURVEYノードのゲート用） */
  answeredIds?: ReadonlySet<string>
  /** 動画紐づきアンケート（動画ノードのシート提示状態・ADR-0014） */
  videoSurvey?: SurveySheetState
  /**
   * 動画紐づきアンケートを動画に重ねず、この要素へ portal して描画する。
   * BOX の PC（2カラム）が右パネルの枠を渡す（動画の下に被せない・ADR-0016）。
   * 未指定なら従来どおり動画下部のシートとして重ねる。
   */
  surveyPortalElement?: HTMLElement | null
  /** SURVEYノードのゲートで設問に回答した */
  onSurveyAnswered?: (questionId: string) => void
  /** SURVEYノードのゲートを送信で通過した（回答を渡し、遷移先の解決はプレイヤーが行う・#265） */
  onSurveyComplete?: (answers: RoutingAnswer[]) => void
  onPlayPause: () => void
  onButtonSelect: (buttonId: string) => void | Promise<void>
  /** 動画内のクリック領域が押された（ワイド型のみ・ADR-0022） */
  onHotspotSelect?: (hotspotId: string) => void | Promise<void>
  onBack: () => void
  videoRef: RefObject<HTMLVideoElement | null>
  onTimeUpdate?: (currentTime: number, duration: number) => void
  onVideoEnd?: (nodeId: string) => void
  onToggleMute: () => void
  /** 音量バーを動かした（ワイド型の操作バー） */
  onVolumeChange?: (volume: number) => void
  /** 音あり自動再生がブロックされ、ミュートに落ちたときのUI同期通知 */
  onAutoplayMuted?: () => void
  onPlaybackRateChange: (rate: number) => void
  onExternalLink?: (url: string, openInNewTab: boolean) => void
  /**
   * 動画の object-fit。未指定は 'cover'（従来どおり全面を埋める）。
   * BOX の1カラムがタブレット幅で 'contain' を渡し、テロップの欠けを防ぐ。
   */
  videoFit?: 'cover' | 'contain'
  VideoCanvas: typeof VideoCanvas
  ButtonOverlay: typeof ButtonOverlay
  ProgressBar: typeof ProgressBar
  onClose?: () => void
  isDesktop: boolean
  containerMode?: boolean
  /** 親コンテナ内でも POPUP と同じ角丸・影を維持する（管理画面プレビュー用） */
  standaloneAppearance?: boolean
  /** 「戻る」ボタンを出さない（BOX/WIDE は分岐ボタンだけで進む・ADR-0016） */
  hideBackButton?: boolean
  /** 動画上の分岐ボタンを出さない（BOX の PC は右パネルに置く・デモA準拠）。テロップは残す */
  hideBranchButtons?: boolean
  /** 見た目。`wide` は横型16:9のインライン再生（ADR-0016） */
  variant?: 'default' | 'wide'
  /** ワイド型の並べ方（'stacked' = スマホ縦画面で選択肢を動画の下に並べる） */
  wideLayout?: 'overlay' | 'stacked'
  /** ワイド型の目次の置き方（'side' = 動画の左に重ね、操作バーと一緒に出る／'overlay' = ボタンから重ねて開く・ADR-0025） */
  wideAgendaPlacement?: 'side' | 'overlay'
  /** 目次を押せなくする間（必須アンケートの強制提示中） */
  isAgendaLocked?: boolean
  /** 末尾に残っている未回答の必須設問数。0 になるまで終了画面/分岐を出さない */
  pendingEndSurveyCount?: number
  isFullscreen?: boolean
  onSeek?: (time: number) => void
  onToggleFullscreen?: () => void
}

const InteractiveVideoPlayerPresentation: FC<InteractiveVideoPlayerPresentationProps> = ({
  config,
  state,
  scenarioId,
  answeredIds,
  videoSurvey,
  surveyPortalElement,
  onSurveyAnswered,
  onSurveyComplete,
  onPlayPause,
  onButtonSelect,
  onHotspotSelect,
  onBack,
  videoRef,
  onTimeUpdate,
  onVideoEnd,
  onToggleMute,
  onVolumeChange,
  onAutoplayMuted,
  onPlaybackRateChange,
  videoFit,
  VideoCanvas,
  ButtonOverlay,
  ProgressBar,
  onClose,
  isDesktop,
  containerMode,
  standaloneAppearance,
  hideBackButton,
  hideBranchButtons,
  variant = 'default',
  wideLayout = 'overlay',
  wideAgendaPlacement = 'overlay',
  isAgendaLocked = false,
  pendingEndSurveyCount = 0,
  isFullscreen = false,
  onSeek,
  onToggleFullscreen,
}) => {
  const currentNode = config.nodes.get(state.currentNodeId)
  const isSurveyGate = currentNode?.type === 'SURVEY'
  const isWide = variant === 'wide'
  // スマホ縦画面のワイド型。16:9 の映像は小さくなるので、選択肢・アンケートは映像に重ねず下に並べる
  const isStacked = isWide && wideLayout === 'stacked'
  // 縦型でアンケートを開いている間は、タイル型スマホの「詳しく見る」と同じく動画を上 1/3 に縮め、
  // 残りをアンケートに使う。動画に重ねると話している人が途中で切れ、選択肢の下に空白も残るため。
  const isSurveyDocked =
    !isWide && !isSurveyGate && !surveyPortalElement && Boolean(videoSurvey?.expanded && videoSurvey.activeQuestion)
  // WIDE型の選択肢はノードごとの設定で、最初・指定時刻・末尾のいずれかに表示する。
  // 「末尾」は動画終了の BRANCH_CHOICE_LEAD_SEC 秒前から出して終了後も残す（再生中に出し始める。
  // 終了後に初めて出す形は作らない・ADR-0023 A-1・branchChoiceTiming.ts）。
  // 必須アンケートの強制提示中は、回答せず遷移で逃げられないよう選択肢を隠す（ADR-0014 踏襲）。
  // 末尾の必須アンケートが残っているうちも出さない（先に答えさせる）。
  const branchCueTime = currentNode?.branchCueTime ?? null
  // 指定時刻が終了3秒前より後（動画より長い時刻・差し替えで尺が縮んだ等）なら「末尾」と同じ扱いにし、
  // 終了後に初めて出す形にしない
  const branchCueReached =
    branchCueTime === null || isCueTooLate(branchCueTime, state.duration)
      ? state.hasEnded || isWithinEndLead(state.currentTime, state.duration)
      : branchCueTime === 0 || state.currentTime >= branchCueTime || state.hasEnded
  const endOverlayReady =
    isWide && state.hasEnded && !(videoSurvey?.expanded && videoSurvey.isForced) && pendingEndSurveyCount === 0
  const choiceOverlayReady =
    isWide && branchCueReached && !(videoSurvey?.expanded && videoSurvey.isForced) && pendingEndSurveyCount === 0
  // 「分岐」は別の動画へ進む選択肢のこと。外部リンクしか無いノードは行き止まり＝終了画面。
  // 動画への分岐がある場合は、BOX型と同様に外部リンクを含む全ボタンを選択肢として表示する。
  const wideChoiceButtons = currentNode?.buttons ?? []
  const branchButtons = (currentNode?.buttons ?? []).filter(
    (button) => button.type === ButtonActionType.VIDEO_TRANSITION,
  )
  const linkButton = (currentNode?.buttons ?? []).find((button) => button.type === ButtonActionType.EXTERNAL_LINK)
  const showWideBranch =
    choiceOverlayReady && (branchButtons.length > 0 || (branchCueTime !== null && wideChoiceButtons.length > 0))
  // ワイド型の目次（ADR-0025）。目次をオンにした動画では、その動画のインタラクティブボタンを
  // 動画の上ではなく目次に並べる（同じボタンを2か所に出さない）。オフの動画（イントロなど）は何も出さない。
  // PC の広い画面は動画の左に重ね、下の操作バーと一緒に出る・隠れる（隠れている間は左上の「目次」ボタンだけ残す）。
  // 動画の横に並べると枠の幅を取り合って動画が小さくなるので、重ねて動画を最大サイズのまま見せる。
  // 狭い画面は左上のボタン、スマホ縦画面は動画の下のボタンから開く。
  const agendaEntries =
    isWide && (config.agenda ?? []).some((item) => item.nodeId === state.currentNodeId)
      ? toAgendaEntries(currentNode?.hotspots ?? [])
      : []
  const hasAgenda = agendaEntries.length > 0
  // 再生バーの目印は、目次の行のうち同じ動画の場面へ移るものから作る（ADR-0026）。
  // 以前は動画が持つチャプターから作っていたが、目次と2つあって分かりにくいため1つにまとめた
  const agendaMarks = agendaEntries
    .flatMap((entry) => (entry.action.type === 'seek' ? [{ time: entry.action.time, title: entry.label }] : []))
    .sort((a, b) => a.time - b.time)
  const currentAgendaId = hasAgenda ? findCurrentAgendaEntryId(agendaEntries, state.currentTime) : null
  const nodeHotspots = isWide && !isSurveyGate ? (currentNode?.hotspots ?? []) : []
  // 映像の上に出すボタン。目次をオンにした動画では、目次の行（0秒から出す）を除き、途中から出るボタン
  // （「動画の最後」で足した最後の選択肢など）だけを映像に出す
  const wideHotspots = hasAgenda ? nodeHotspots.filter(isShownOnVideoWithAgenda) : nodeHotspots
  // 最後まで出す領域は終了後もその場に残し、そのあいだは終了画面を出さない。
  // ・同じ動画内へ移動する領域
  // ・終了3秒前より前から出ている「別の動画へ」（分岐の選択肢と同じく、再生中から出たボタンが残るだけ・ADR-0023 A-1）。
  //   終了画面に「次の動画へ」を並べない（A-3）ため、残すボタンがある動画では終了画面を出さない。
  //   目次の行は映像に出さないので、目次だけの動画はこれまでどおり終了画面にする
  const hasKeptTransitionHotspots = wideHotspots.some(
    (hotspot) => hotspot.action.type === 'video_transition' && isTransitionKeptAfterEnd(hotspot, state.duration),
  )
  const hasEndHotspots =
    hasKeptTransitionHotspots ||
    wideHotspots.some((hotspot) => hotspot.action.type === 'seek' && isHotspotAtVideoEnd(hotspot, state.duration))
  const showWideEndScreen = endOverlayReady && branchButtons.length === 0 && branchCueTime === null && !hasEndHotspots

  // 分岐・アンケートで覆われている間は、下にある領域を押せないようにする。
  const showWideHotspots =
    wideHotspots.length > 0 && !showWideBranch && !showWideEndScreen && !videoSurvey?.expanded
  // 目次のある動画で、映像の上のボタンが出ている間。左に重ねた目次を閉じ、ボタンが目次の下に隠れないようにする
  // （選択肢を出している間に目次を重ねないのと同じ）。左上の「目次」ボタンからいつでも開き直せる
  const isVideoButtonShowingWithAgenda =
    hasAgenda &&
    showWideHotspots &&
    wideHotspots.some(
      (hotspot) => state.hasEnded || (state.currentTime >= hotspot.startTime && state.currentTime <= hotspot.endTime),
    )
  const isAgendaSide = hasAgenda && !isStacked && wideAgendaPlacement === 'side'
  const [isAgendaCollapsed, setIsAgendaCollapsed] = useState(false)
  const [isAgendaOpen, setIsAgendaOpen] = useState(false)
  const [isAgendaHovered, setIsAgendaHovered] = useState(false)
  // 下の操作バーが出ているか（バーが自動で隠れるのに合わせて、重ねた目次も隠す）
  const [isControlsVisible, setIsControlsVisible] = useState(true)
  // 終了後に「別の動画へ」のボタンを残す動画も、分岐の選択肢と同じく操作バーを残す（終了画面が無いので、見直しはここから）
  const isControlsBarShown = isWide && !isSurveyGate && (!state.hasEnded || showWideBranch || hasKeptTransitionHotspots)
  // 選択肢・終了画面が出ている間は、そちらを隠さないよう目次は重ねない
  const showAgendaOverlay =
    isAgendaSide &&
    !isAgendaCollapsed &&
    !showWideBranch &&
    !isVideoButtonShowingWithAgenda &&
    !showWideEndScreen &&
    ((isControlsBarShown && isControlsVisible) || isAgendaHovered)
  const showAgendaButton = !isAgendaSide || (!showAgendaOverlay && !showWideEndScreen)
  // 左上の「目次」ボタン。選択肢・終了画面が出ている間は、これまでどおりそれらの下に隠す（上に出さない）
  const showAgendaChip = hasAgenda && !isStacked && showAgendaButton && !showWideBranch && !showWideEndScreen
  // 2本目以降の「← 前の動画へ」。アンケートの入口（全画面）と、答えないと進めない設問を出している間は出さない
  const showWideBackButton =
    isWide &&
    !hideBackButton &&
    state.history.length > 0 &&
    !isSurveyGate &&
    !(videoSurvey?.expanded && videoSurvey.isForced)
  const currentAgendaLabel = agendaEntries.find((entry) => entry.id === currentAgendaId)?.label ?? currentNode?.title
  const renderAgendaPanel = (
    headerAction: { kind: 'collapse' | 'close'; onClick: () => void },
    style: React.CSSProperties,
  ) => (
    <WideAgendaPanel
      entries={agendaEntries}
      currentId={currentAgendaId}
      // アンケートの箱（全画面ゲート）・必須アンケートからは、答えずに移らせない
      disabled={isAgendaLocked || isSurveyGate}
      cta={currentNode?.ctaUrl ? { url: currentNode.ctaUrl, label: currentNode.ctaLabel || '詳しく見る' } : null}
      headerAction={headerAction}
      backAction={showWideBackButton ? { onClick: onBack } : undefined}
      onSelect={(id) => {
        setIsAgendaOpen(false)
        void onHotspotSelect?.(id)
      }}
      style={style}
    />
  )
  // 目次を動画の左に重ねている間は、戻るボタンを目次の中（いちばん下の行）に出すので、左上には出さない
  const showWideBackChip = showWideBackButton && !(isAgendaSide && showAgendaOverlay)

  return (
    <div
      data-testid="interactive-video-player"
      className={
        containerMode
          ? 'relative w-full h-full'
          : 'fixed inset-0 md:inset-auto md:right-0 md:bottom-0 w-screen h-screen md:w-[340px] md:h-[650px] z-[9999]'
      }
      style={
        containerMode
          ? { position: 'relative', width: '100%', height: '100%' }
          : {
              position: 'fixed',
              zIndex: 9999,
              pointerEvents: 'auto',
              // PC画面（768px以上）の場合は右下に配置
              ...(isDesktop
                ? {
                    right: '23px',
                    bottom: '23px',
                    top: 'auto',
                    left: 'auto',
                    width: '340px',
                    height: '650px',
                  }
                : {
                    inset: '0',
                    width: '100vw',
                    height: '100vh',
                  }),
            }
      }
    >
      {/* Video Wrapper - Responsive: Full screen on mobile, right side panel on desktop.
          角丸・影を付けるのは、プレイヤー自身が単体のカードとして置かれるとき（POPUP 等）だけ。
          ワイド型は枠いっぱいに動画を見せてページへ溶け込ませる。
          containerMode（BOX / WIDE のモーダル内）は外側のモーダルが既にカードなので、
          ここで角丸を付けると内側の辺まで丸まり、隣の動画テキストパネルとの間に
          切り欠きの余白ができる。外側の角は親の overflow: hidden が処理する。 */}
      <div
        data-testid="video-wrapper"
        className={
          isWide || (containerMode && !standaloneAppearance)
            ? 'w-full h-full bg-black overflow-hidden relative flex flex-col'
            : 'w-full h-full bg-black overflow-hidden relative md:rounded-2xl md:shadow-2xl flex flex-col'
        }
      >
        {/* 動画エリア */}
        <div
          data-testid="video-area"
          className={
            isStacked
              ? 'relative aspect-video w-full shrink-0'
              : isSurveyDocked
                ? 'relative h-1/3 w-full shrink-0'
                : 'relative w-full min-h-0 flex-1'
          }
        >
          {/* SURVEYノード（全画面ゲート・#263）は動画の代わりにゲートフォームを描画する */}
          {isSurveyGate && currentNode ? (
            <SurveyGate
              node={currentNode}
              scenarioId={scenarioId}
              answeredIds={answeredIds ?? new Set()}
              onAnswered={(questionId) => onSurveyAnswered?.(questionId)}
              onComplete={(answers) => onSurveyComplete?.(answers)}
            />
          ) : (
            <>
              {/* Video Canvas */}
              {currentNode && (
                <VideoCanvas
                  videoUrl={currentNode.videoUrl}
                  hlsUrl={currentNode.hlsUrl}
                  // 署名付きURLは再取得で付け替わるので、同じ動画かの判定にはノードIDを使う
                  mediaId={state.currentNodeId}
                  isPlaying={state.isPlaying}
                  isMuted={state.isMuted}
                  volume={state.volume}
                  autoplay={config.autoplay}
                  playbackRate={state.playbackRate}
                  onPlayPause={onPlayPause}
                  onTimeUpdate={(currentTime, duration) => {
                    onTimeUpdate?.(currentTime, duration)
                  }}
                  onEnded={() => {
                    onVideoEnd?.(state.currentNodeId)
                  }}
                  videoRef={videoRef}
                  // 縮めている間は全体が見えるように収める（cover だと横長に切り取られ顔が切れる）
                  fit={isWide || isSurveyDocked ? 'contain' : (videoFit ?? 'cover')}
                  // ワイド型は終了時に分岐オーバーレイ／終了画面を出すので、
                  // 中央の丸いリプレイボタンとは取り合いになる
                  // BOX は選択／再視聴を隣接パネルに集約するため、動画中央の大きな
                  // リプレイボタンで出演者を隠さない。hideBranchButtons はそのモードの印。
                  suppressEndedOverlay={isWide || Boolean(hideBranchButtons)}
                  onAutoplayMuted={onAutoplayMuted}
                />
              )}

              {/* Progress Bar - Instagram Stories style at top。
                  ワイド型は下部のコントロールバーがシーク可能な進捗を持つので出さない。
                  縦型は下部に出演者カード/テロップが入りコントロールを置けない（ADR-0016）ため、
                  この上端バーが唯一の再生位置の手がかりになる。シーク可能にし、
                  ホバー中だけ経過/総尺を出して「何分の動画か」が分かるようにする。 */}
              {config.showProgress && !isWide && (
                <ProgressBar
                  currentTime={state.currentTime}
                  duration={state.duration}
                  isSeekable={true}
                  showTimeStamps={true}
                  onSeek={onSeek}
                />
              )}

              {/* 動画内のクリック領域: 焼き込まれたボタンの上に透明な当たり判定を重ねる（ADR-0022）。
                  見た目を持つ領域はプレイヤーがボタンを描く（ADR-0024）。
                  下部のコントロールバー（zIndex 20）と分岐（30）より下に置く。 */}
              {showWideHotspots && (
                <WideHotspotLayer
                  hotspots={wideHotspots}
                  videoRef={videoRef}
                  onSelect={(hotspotId) => onHotspotSelect?.(hotspotId)}
                  brandColor={config.defaultButtonColor}
                />
              )}

              {/* ワイド型の分岐: 映像を消さずに暗転＋質問＋選択肢を重ねる（ADR-0016）。
                  「末尾」は終了直前の再生中に出し始め、終了後は最終フレームの上に残す。 */}
              {showWideBranch && currentNode && !isStacked && (
                <WideBranchOverlay
                  buttons={wideChoiceButtons}
                  questionText={currentNode.branchQuestionText}
                  defaultButtonColor={config.defaultButtonColor}
                  // 選択肢表示中は操作バーを常に出すので、その上に選択肢を持ち上げる
                  bottomOffset={WIDE_CONTROLS_CLEARANCE}
                  // 選択肢の間は下部のアンケート帯を隠すので、入口を選択肢の上に出す
                  surveyEntry={
                    videoSurvey?.showTab && !surveyPortalElement
                      ? { total: videoSurvey.progress.total, onOpen: videoSurvey.onOpenTab }
                      : null
                  }
                  onSelect={onButtonSelect}
                />
              )}

              {/* ワイド型の終了画面: 主CTAは1つに絞り、再視聴を必ず残す（ADR-0016） */}
              {showWideEndScreen && currentNode && (
                <WideEndScreen
                  // ノードに置いた外部リンクボタンがあればそれを主CTAにする（作者が
                  // 「この動画の最後はここへ」と明示したもの）。無ければ動画CTA（ADR-0015）。
                  ctaUrl={linkButton ? null : (currentNode.ctaUrl ?? null)}
                  ctaLabel={currentNode.ctaLabel ?? null}
                  linkButton={linkButton ?? null}
                  onSelectButton={onButtonSelect}
                  onReplay={() => onSeek?.(0)}
                />
              )}

              {/* Button Overlay。必須設問の強制提示中（ポーズ＋シート展開）は遷移ボタンを
                  隠し、回答せず遷移で逃げられないようにする（ADR-0014）。任意設問の展開中は
                  シートが上に被さるだけで、遷移の自由は残す。
                  ワイド型は上の WideBranchOverlay が担うので出さない。 */}
              {!isWide && currentNode && !isSurveyDocked && !(videoSurvey?.expanded && videoSurvey.isForced) && (
                <ButtonOverlay
                  buttons={hideBranchButtons ? [] : currentNode.buttons}
                  onButtonSelect={onButtonSelect}
                  onBack={onBack}
                  telops={currentNode.telops}
                  currentTime={state.currentTime}
                  currentNodeId={state.currentNodeId}
                  previousNodeId={
                    hideBackButton || state.history.length === 0 ? undefined : state.history[state.history.length - 1]
                  }
                  isEmbedded={true}
                  defaultButtonColor={config.defaultButtonColor}
                  // 下のアンケートの帯（SPSurveyLayer）と重ならないよう、帯が出ている間はボタンを上げる
                  reserveSurveyBar={Boolean(videoSurvey?.showTab && !surveyPortalElement)}
                />
              )}

              {/* BOX の PC（2カラム）は動画に重ねず、右の白いパネルへ portal して出す。
                  縦型動画に被せると出演者が隠れるため（ADR-0016）。 */}
              {videoSurvey && surveyPortalElement
                ? createPortal(<PanelSurvey survey={videoSurvey} />, surveyPortalElement)
                : null}

              {/* 動画紐づきアンケート（ADR-0014）: SLIDER/TILE のスマホ表示と同じく
                  下からスライドするシートで提示する。任意設問はオプトインピルで案内し、
                  展開時は動画下部にシートを重ねる（動画は useSurveySheet がポーズ制御）。 */}
              {/* 外枠は動画操作を妨げないようクリックを素通りさせ、案内の帯だけクリックを受け取る
                  （pointerEvents: 'auto' が無いと帯を押しても下の動画に届き、一時停止するだけになる）。 */}
              {videoSurvey &&
                !surveyPortalElement &&
                (isWide ? (
                  // ワイド型は入口が常に映像の上に重なるので、帯ではなく選択肢表示中と同じ
                  // 濃紺のボタンに揃え、操作バーの上・右端に固定で置く（バーが隠れても動かさない）。
                  // 選択肢・終了画面が出ている間は、選択肢の上の入口（WideBranchOverlay）に任せる。
                  videoSurvey.showTab &&
                  !isStacked &&
                  !showWideBranch &&
                  !showWideEndScreen && (
                    <WideSurveyEntryButton
                      total={videoSurvey.progress.total}
                      onOpen={videoSurvey.onOpenTab}
                      style={{
                        position: 'absolute',
                        right: '18px',
                        bottom: `${WIDE_CONTROLS_CLEARANCE + 16}px`,
                        zIndex: 25,
                      }}
                    />
                  )
                ) : isSurveyDocked ? null : (
                  // 縦型も下端にシークバーを置いているので、同じくその上へ逃がす。
                  // 32px = バー(4〜6px) + 下padding(8px) + ホバー時のタイムスタンプ(約20px)。
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      bottom: '32px',
                      left: 0,
                      zIndex: 25,
                      pointerEvents: 'none',
                    }}
                  >
                    <div style={{ pointerEvents: 'auto' }}>
                      <SPSurveyLayer survey={videoSurvey} alignWithBranchButtons />
                    </div>
                  </div>
                ))}
              {videoSurvey?.expanded &&
                videoSurvey.activeQuestion &&
                !surveyPortalElement &&
                !isStacked &&
                (isWide && isDesktop ? (
                  // ワイド型の PC: 中央に半透明パネル＋背景ぼかし。枠は 16:9 のまま動かさない（ADR-0016）
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      // 選択肢表示中の操作バー（31）より前に出す
                      zIndex: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(0,0,0,0.35)',
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    <div
                      style={{
                        width: 'min(480px, 92%)',
                        maxHeight: '88%',
                        overflowY: 'auto',
                        borderRadius: '14px',
                        overflow: 'hidden',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                      }}
                    >
                      <SurveySheetHost survey={videoSurvey} />
                    </div>
                  </div>
                ) : isWide ? (
                  <div className="absolute inset-x-0 bottom-0 z-30 h-[60%]">
                    <SurveySheetHost survey={videoSurvey} />
                  </div>
                ) : null)}
            </>
          )}

          {/* ワイド型の目次。広い画面は動画の左に重ねて操作バーと一緒に出し、狭い画面は左上のボタンから開く */}
          {hasAgenda && !isStacked && (
            <>
              {isAgendaSide && (
                <div
                  data-testid="wide-agenda-overlay"
                  inert={!showAgendaOverlay}
                  onPointerEnter={() => setIsAgendaHovered(true)}
                  onPointerLeave={() => setIsAgendaHovered(false)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    // 背景は動画の下端まで伸ばす。操作バー（zIndex 20）より下に置き、再生ボタンなどは押せるままにする
                    bottom: 0,
                    width: WIDE_AGENDA_SIDE_WIDTH_CSS,
                    maxWidth: '45%',
                    zIndex: 19,
                    opacity: showAgendaOverlay ? 1 : 0,
                    transform: showAgendaOverlay ? 'none' : 'translateX(-16px)',
                    pointerEvents: showAgendaOverlay ? 'auto' : 'none',
                    transition: 'opacity 0.3s, transform 0.3s',
                  }}
                >
                  {renderAgendaPanel(
                    {
                      kind: 'collapse',
                      onClick: () => {
                        setIsAgendaCollapsed(true)
                        setIsAgendaHovered(false)
                      },
                    },
                    {
                      height: '100%',
                      // 一覧の中身は操作バーの上で止め、バーに隠れないようにする
                      paddingBottom: `${WIDE_CONTROLS_CLEARANCE + 12}px`,
                      background: WIDE_AGENDA_OVERLAY_BACKGROUND,
                    },
                  )}
                </div>
              )}
              {!isAgendaSide && isAgendaOpen && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 40 }}>
                  <div
                    aria-hidden="true"
                    onClick={() => setIsAgendaOpen(false)}
                    style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }}
                  />
                  {renderAgendaPanel(
                    { kind: 'close', onClick: () => setIsAgendaOpen(false) },
                    { position: 'absolute', top: 0, bottom: 0, left: 0, width: `min(${WIDE_AGENDA_PANEL_WIDTH + 32}px, 82%)` },
                  )}
                </div>
              )}
            </>
          )}

          {/* ワイド型の左上。2本目以降は「← 前の動画へ」を再生中から出し続け（終了後も残す）、
              目次のある動画では「目次」ボタンを横に並べる。目次を動画の左に重ねている間は、
              戻るボタンは目次のいちばん下の行に入れる（動画の上に浮かせて目次からはみ出させない） */}
          {isWide && (showWideBackChip || showAgendaChip) && (
            <div
              style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                // 管理画面のプレビューは右上に閉じるボタンがあるので、その分を空ける（入らない分は動画名を「…」で省く）
                right: '56px',
                // 選択肢・終了画面（zIndex 30）の上に出す
                zIndex: 31,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                pointerEvents: 'none',
              }}
            >
              {showWideBackChip && (
                <div style={{ display: 'flex', minWidth: 0, pointerEvents: 'auto' }}>
                  <WideBackButton onClick={onBack} />
                </div>
              )}
              {showAgendaChip && (
                <div style={{ display: 'flex', pointerEvents: 'auto' }}>
                  <WideAgendaButton
                    inline
                    position={formatAgendaPosition(agendaEntries, currentAgendaId)}
                    onClick={() => (isAgendaSide ? setIsAgendaCollapsed(false) : setIsAgendaOpen(true))}
                  />
                </div>
              )}
            </div>
          )}

          {/* Video Controls。ワイド型はシーク・目次の目印・全画面を持つ下部バー、
              それ以外は右上のミュート／速度／閉じるパネル。 */}
          {isWide ? (
            // 選択肢・終了後も残すボタンが出ていれば終了後もバーを残す（全画面解除・ミュート・見直しができるように）
            isControlsBarShown && (
              <WideControlsBar
                // 選択肢表示中・一時停止中は常に出す。再生中は操作が無ければ自動で隠す
                pinned={showWideBranch || !state.isPlaying}
                onVisibleChange={setIsControlsVisible}
                isPlaying={state.isPlaying}
                isMuted={state.isMuted}
                volume={state.volume}
                currentTime={state.currentTime}
                duration={state.duration}
                playbackRate={state.playbackRate}
                isFullscreen={isFullscreen}
                chapters={agendaMarks}
                // 終了後の再生ボタンは最初から再生し直す（終了画面の「もう一度見る」と同じ）
                onPlayPause={state.hasEnded ? () => onSeek?.(0) : onPlayPause}
                onToggleMute={onToggleMute}
                onVolumeChange={(volume) => onVolumeChange?.(volume)}
                onSeek={(time) => onSeek?.(time)}
                onPlaybackRateChange={onPlaybackRateChange}
                onToggleFullscreen={() => onToggleFullscreen?.()}
              />
            )
          ) : (
            <VideoControlsPanel
              isMuted={state.isMuted}
              playbackRate={state.playbackRate}
              onToggleMute={onToggleMute}
              onPlaybackRateChange={onPlaybackRateChange}
              onClose={onClose}
            />
          )}
        </div>

        {/* 縦型のアンケート: 動画を縮めた下の残りに置く（isSurveyDocked） */}
        {isSurveyDocked && videoSurvey && (
          <div className="relative z-30 min-h-0 flex-1 overflow-hidden">
            <SurveySheetHost survey={videoSurvey} />
          </div>
        )}

        {/* スマホ縦画面のワイド型の目次: 動画の下に今の位置とボタンを出し、押すと下から開く */}
        {isStacked && hasAgenda && (
          <div
            data-testid="wide-agenda-bar"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 12px',
              background: '#0b1220',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              color: '#fff',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '11px', opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
                {formatAgendaPosition(agendaEntries, currentAgendaId)}
              </span>
              <span
                style={{
                  display: 'block',
                  overflow: 'hidden',
                  fontSize: '14px',
                  fontWeight: 800,
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {currentAgendaLabel}
              </span>
            </div>
            <button
              type="button"
              data-testid="wide-agenda-button"
              onClick={() => setIsAgendaOpen(true)}
              style={{
                display: 'inline-flex',
                flexShrink: 0,
                alignItems: 'center',
                gap: '6px',
                padding: '7px 14px',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '999px',
                background: 'transparent',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <List size={14} />
              目次
            </button>
          </div>
        )}
        {isStacked && hasAgenda && isAgendaOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }}>
            <div
              aria-hidden="true"
              onClick={() => setIsAgendaOpen(false)}
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }}
            />
            {renderAgendaPanel(
              { kind: 'close', onClick: () => setIsAgendaOpen(false) },
              {
                position: 'absolute',
                right: 0,
                bottom: 0,
                left: 0,
                maxHeight: '72%',
                borderRadius: '16px 16px 0 0',
                paddingBottom: 'env(safe-area-inset-bottom)',
              },
            )}
          </div>
        )}

        {/* スマホ縦画面のワイド型: 選択肢・アンケートは映像に重ねず動画の下に並べる */}
        {isStacked && currentNode && !isSurveyGate && (
          <WideStackedPanel
            showChoices={showWideBranch}
            buttons={wideChoiceButtons}
            questionText={currentNode.branchQuestionText}
            defaultButtonColor={config.defaultButtonColor}
            surveyEntry={
              videoSurvey?.showTab && !surveyPortalElement
                ? { total: videoSurvey.progress.total, onOpen: videoSurvey.onOpenTab }
                : null
            }
            survey={surveyPortalElement ? null : videoSurvey}
            onSelect={onButtonSelect}
          />
        )}
      </div>
    </div>
  )
}

export default InteractiveVideoPlayerPresentation
